import React from "react";
import { StatusPill, DivisionPill, DIVISION_LABEL } from "./StatusPill";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (e) {
        return iso;
    }
};

const Cell = ({ children, className = "" }) => (
    <td className={`whitespace-nowrap px-3 py-2.5 align-middle text-[13px] ${className}`}>
        {children || <span className="text-text-muted">—</span>}
    </td>
);

export const LeadsTable = ({ items, onRowClick, loading, page, totalPages, onPrev, onNext }) => {
    if (loading) {
        return (
            <div
                className="rounded-xl border border-border-subtle bg-elevated p-12 text-center text-[13px] text-text-muted"
                data-testid="leads-table-loading"
            >
                Loading leads…
            </div>
        );
    }
    if (!items || items.length === 0) {
        return (
            <div
                className="rounded-xl border border-border-subtle bg-elevated p-16 text-center"
                data-testid="leads-table-empty"
            >
                <p className="font-display text-[18px] font-semibold text-text-primary">
                    No leads match these filters.
                </p>
                <p className="mt-2 text-[14px] text-text-secondary">
                    Try clearing the filters or adjusting the search.
                </p>
            </div>
        );
    }

    return (
        <div data-testid="leads-table-wrapper">
            {/* Desktop table */}
            <div
                className="hidden overflow-x-auto rounded-xl border border-border-subtle bg-elevated lg:block"
                data-testid="leads-table"
            >
                <table className="min-w-full text-left">
                    <thead className="sticky top-0 z-10 bg-[var(--bg-elevated-2)] text-[11px] uppercase tracking-widest-2 text-text-muted">
                        <tr>
                            <th className="px-3 py-3">Submitted</th>
                            <th className="px-3 py-3">Division</th>
                            <th className="px-3 py-3">Name</th>
                            <th className="px-3 py-3">Email</th>
                            <th className="px-3 py-3">Phone</th>
                            <th className="px-3 py-3">Business</th>
                            <th className="px-3 py-3">City</th>
                            <th className="px-3 py-3">State</th>
                            <th className="px-3 py-3">Amount / Deal</th>
                            <th className="px-3 py-3">UTM</th>
                            <th className="px-3 py-3">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle">
                        {items.map((r) => (
                            <tr
                                key={`${r.source}:${r.id}`}
                                onClick={() => onRowClick(r)}
                                data-testid={`lead-row-${r.id}`}
                                className="cursor-pointer transition-colors duration-150 hover:bg-[var(--bg-elevated-2)]"
                            >
                                <Cell className="text-text-secondary">{formatDate(r.submittedAt)}</Cell>
                                <Cell><DivisionPill division={r.division} /></Cell>
                                <Cell className="text-text-primary font-medium">{r.name}</Cell>
                                <Cell>{r.email}</Cell>
                                <Cell>{r.phone}</Cell>
                                <Cell>{r.businessName}</Cell>
                                <Cell>{r.city}</Cell>
                                <Cell>{r.state}</Cell>
                                <Cell>{r.amountOrDeal}</Cell>
                                <Cell>
                                    {r.utmSource ? (
                                        <span className="text-text-secondary">
                                            {r.utmSource}
                                            {r.utmCampaign ? ` · ${r.utmCampaign}` : ""}
                                        </span>
                                    ) : null}
                                </Cell>
                                <Cell><StatusPill status={r.status} /></Cell>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile card list */}
            <ul className="space-y-3 lg:hidden" data-testid="leads-mobile-list">
                {items.map((r) => (
                    <li key={`${r.source}:${r.id}`}>
                        <button
                            type="button"
                            onClick={() => onRowClick(r)}
                            className="w-full rounded-xl border border-border-subtle bg-elevated p-4 text-left"
                            data-testid={`lead-card-${r.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="truncate font-medium text-text-primary">
                                        {r.name || "—"}
                                    </p>
                                    <p className="truncate text-[13px] text-text-secondary">
                                        {r.email}
                                    </p>
                                </div>
                                <StatusPill status={r.status} />
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[12px] text-text-muted">
                                <span>{DIVISION_LABEL[r.division] || r.division}</span>
                                <span>{formatDate(r.submittedAt)}</span>
                            </div>
                        </button>
                    </li>
                ))}
            </ul>

            {/* Pagination */}
            {totalPages > 1 && (
                <div
                    className="mt-5 flex items-center justify-between"
                    data-testid="leads-pagination"
                >
                    <button
                        type="button"
                        onClick={onPrev}
                        disabled={page <= 1}
                        data-testid="page-prev"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-40 hover:text-text-primary hover:border-border-default"
                    >
                        ← Prev
                    </button>
                    <span className="text-[12.5px] text-text-muted tabular-nums">
                        Page {page} of {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={onNext}
                        disabled={page >= totalPages}
                        data-testid="page-next"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border-subtle px-3 py-1.5 text-[13px] text-text-secondary disabled:opacity-40 hover:text-text-primary hover:border-border-default"
                    >
                        Next →
                    </button>
                </div>
            )}
        </div>
    );
};

export default LeadsTable;
